import { ReactNode, type ReactElement, useEffect } from 'react';
import { FormProvider, type FieldValues, type UseFormReturn } from 'react-hook-form';
import FormDialog from '../FormDialog';
import {
  ImperativeFormDialogRefType,
  useGetImperativeOpen,
} from '@shared/components/UserFormDialog';
import { useIntlTranslations } from '@libs/intl/intl.hooks';
import { useAddSnackbarMessage } from '@shared/useAddSnackBar';

type SubmitFormDialogProps<T extends FieldValues> = {
  ref: React.Ref<ImperativeFormDialogRefType>;
  methods: UseFormReturn<T>;
  actions?: ReactNode;
  onSubmit: (data: T) => false | void;
  validate?: (data: T) => true | string;
  onOpen?: () => void;
  title: string;
  dialogHelperText?: string;
  children:
    | ((
        handleInputRef: (fieldPath: keyof T) => (el: HTMLInputElement | null) => void,
      ) => ReactElement)
    | ReactNode;
};

export const SubmitFormDialog = <T extends FieldValues>({
  ref,
  methods,
  onSubmit,
  onOpen,
  title,
  validate,
  dialogHelperText,
  actions,
  children,
}: SubmitFormDialogProps<T>) => {
  const { open, close, handleInputRef } = useGetImperativeOpen<T>(ref);
  const addSnackBarMessage = useAddSnackbarMessage();
  const { t } = useIntlTranslations();

  useEffect(() => {
    if (open) onOpen?.();
  }, [open]);

  const handleClose = () => {
    methods.reset();
    close();
  };

  const handleSubmit = (data: T) => {
    const validation = validate?.(data);

    if (typeof validation === 'string') {
      addSnackBarMessage({
        type: 'error',
        text: t(validation),
      });

      return;
    }

    const result = onSubmit(data);

    if (result !== false) {
      handleClose();
    }
  };

  return (
    <FormProvider {...methods}>
      <FormDialog<T>
        isOpen={open}
        onClose={handleClose}
        title={title}
        actions={actions}
        handleSubmit={() => methods.handleSubmit(handleSubmit)}
        dialogHelperText={dialogHelperText}
      >
        {typeof children === 'function' ? children(handleInputRef) : children}
      </FormDialog>
    </FormProvider>
  );
};
